import React, { useMemo, useState } from 'react';
import { TabPanel, Tabs } from 'tea-component';
import TabPanelItem from './TabPanelItem';
import { getShowCount } from '@/pages/opinion/utils';
import SvgIcon from '@/pages/home/components/SvgIcon'; 
import { useIntl } from 'umi'; 
import { LanguageType } from '../../types/common';
import BubbleCommon, {
  HeroGptTips,
  summaryTopicNotSentimentTips,
  TipsMapType,
} from '../BubbleCommon';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import './index.scss';
import { sentimentTypeMap } from '../ReviewsModal/constants';

export function RightInfo({
  language,
  itemClick,
  sentimentCountMap,
  commonParams,
  hasGptPanel = false,
  sortItem = 'comment_time',
  tips = HeroGptTips,
  children,
  extInfo = {},
  filterTabId,
  isSubTopicSentiment = false,
}: {
  language: LanguageType;
  itemClick: (value: any) => void;
  sentimentCountMap: { [key: string]: number };
  commonParams: any;
  hasGptPanel?: boolean;
  sortItem?: 'comment_time' | 'engagement';
  tips?: TipsMapType;
  children?: any;
  extInfo?: { [key: string]: any };
  filterTabId?: string;
  isSubTopicSentiment?: boolean;
}) {
  const { formatMessage } = useIntl();
  const { Sentiment_Tab } = useGetSingePageSourceRule({
    keys: 'Sentiment_Tab',
  });
  const getSentiment = (rating: number, text: string) => {
    const icon = sentimentTypeMap[rating];
    return (
      <div className="tab-flex">
        <SvgIcon className="tab-icon" type={icon} />
        {text}
      </div>
    );
  };

  const [tabs, defaultActiveId, countMap] = useMemo(() => {
    const PV = sentimentCountMap['positive'] || 0,
      NV = sentimentCountMap['neutral'] || 0,
      NEV = sentimentCountMap['negative'] || 0,
      NOT_SENTIMENT = sentimentCountMap['not_sentiment'] || 0;
    const positive = getShowCount(PV);
    const neutral = getShowCount(NV);
    const negative = getShowCount(NEV);
    const not_sentiment = getShowCount(NOT_SENTIMENT);

    let list: Array<{
      id: string;
      label: any;
      sentiment?: Array<any>;
      key: 0 | 1 | 3 | 5 | 'all' | 'GPT';
      subTopicSentiment?: Array<string>;
    }> = [
      {
        id: 'positive',
        key: 5,
        sentiment: [5],
        subTopicSentiment: ['positive'],
        label: getSentiment(
          5,
          `${formatMessage({ id: 'Positive' })} (${positive})`,
        ),
      },
      {
        id: 'neutral',
        key: 3,
        sentiment: [3],
        subTopicSentiment: ['neutral'],
        label: getSentiment(
          3,
          `${formatMessage({ id: 'Neutral' })} (${neutral})`,
        ),
      },
      {
        id: 'negative',
        key: 1,
        sentiment: [1],
        subTopicSentiment: ['negative'],
        label: getSentiment(
          1,
          `${formatMessage({ id: 'Negative' })} (${negative})`,
        ),
      },
    ];

    // 根据 filterTabId 过滤 list
    if (filterTabId) {
      list = list.filter((item) => item.id === filterTabId);
    }

    if (hasGptPanel && !Sentiment_Tab) {
      list.unshift({
        id: 'gpt',
        key: 'GPT',
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SvgIcon type="robot" className="dbo-gpt-summary-btn-icon" />
            {formatMessage({ id: 'AI Summary' })}
            <BubbleCommon placement="top" tipsMap={tips}></BubbleCommon>
          </div>
        ),
      });
    }

    if (isSubTopicSentiment && NOT_SENTIMENT > 0) {
      list.push({
        id: 'not_sentiment',
        key: 0,
        sentiment: [],
        subTopicSentiment: ['not_sentiment'],
        label: (
          <BubbleCommon showIcon={false} tipsMap={summaryTopicNotSentimentTips}>
            {getSentiment(
              0,
              `${formatMessage({ id: 'Unscored' })} (${not_sentiment})`,
            )}
          </BubbleCommon>
        ),
      });
    }

    let defaultValue;
    if (hasGptPanel) {
      defaultValue = 'gpt';
    } else if (positive) {
      defaultValue = 'positive';
    } else if (neutral) {
      defaultValue = 'neutral';
    } else if (negative) {
      defaultValue = 'negative';
    } else {
      defaultValue = 'positive'; // 默认值
    }

    if (Sentiment_Tab) {
      defaultValue = 'sentiment';
      list = [
        {
          id: 'sentiment',
          key: 'all',
          sentiment: [],
          label: formatMessage({ id: 'Reviews' }),
        },
      ];
    }

    return [
      list,
      defaultValue,
      {
        '0': NOT_SENTIMENT,
        '1': NEV,
        '3': NV,
        '5': PV,
        all: NV + NEV + PV + NOT_SENTIMENT,
        GPT: 0,
      },
    ];
  }, [sentimentCountMap, formatMessage]);

  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <div className="table-expandable-detail-r dbo-tablechat">
      <Tabs
        tabs={tabs}
        className="tab-box"
        destroyInactiveTabPanel={false}
        activeId={activeId || defaultActiveId}
        onActive={(value) => {
          setActiveId(value.id);
          buriedBtnPoint('TAB_TopicAnalysisTAB', {
            sentiment: value.id,
            ...extInfo,
          });
        }}
      >
        {tabs.map(({ key, sentiment, id, subTopicSentiment }) => (
          <TabPanel key={id} id={id} className="tab-panel-box">
            {id === 'gpt' ? (
              children
            ) : (
              <TabPanelItem
                commonParams={{
                  ...commonParams,
                  sort_item: sortItem,
                  sort_model: 'desc',
                  sentiment: !isSubTopicSentiment
                    ? sentiment
                    : commonParams.sentiment,
                  sentiment_ratings: isSubTopicSentiment
                    ? subTopicSentiment
                    : [],
                }}
                count={countMap?.[key] || 0}
                itemClick={itemClick}
                language={language}
              ></TabPanelItem>
            )}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
